import sortBy from "lodash.sortby";
import React, { useMemo } from "react";

import { IgTextField, IgTypography } from "@intelligems/ig-theme";
import {
  OrgShopIdDto,
  StripeApiCustomerDto,
  StripeApiGetAllCustomersResponseBodyType,
} from "@intelligems/ig-types";
import { Grid, Skeleton } from "@mui/material";
import { useStripeApi } from "@src/api/stripe/stripe-api";
import { useLoggedInUser } from "@src/api/user-auths/login";
import { VirtualAutoComplete } from "@src/components/VirtualAutoComplete/VirtualAutoComplete";
import { filterByNameOrId } from "@src/features/admin/components/Fields/filter-by-name-or-id";

const Formatter = (data: StripeApiCustomerDto) => {
  return (
    <Grid container columnSpacing={1} justifyContent={"space-between"}>
      <Grid item>
        <IgTypography>{data.name}</IgTypography>
      </Grid>
      {data.hasActiveStripeSubscription && data.hasActiveShopifySubscription ? (
        <Grid item>
          <IgTypography sx={{ fontStyle: "italic" }} variant={"body2"}>
            Has Active Subscription
          </IgTypography>
        </Grid>
      ) : null}
    </Grid>
  );
};

interface ChooseCustomerProps {
  filteredIds?: string[];
  payload: { orgs: OrgShopIdDto[]; customer?: StripeApiCustomerDto | null };
  onChange: (value: StripeApiCustomerDto) => void;
}

export const ChooseCustomerField = ({
  filteredIds,
  payload,
  onChange,
}: ChooseCustomerProps) => {
  const user = useLoggedInUser();
  const customers = useStripeApi<StripeApiGetAllCustomersResponseBodyType>({
    action: "getAllCustomers",
    orgs: payload.orgs,
  });

  const data = useMemo(() => {
    if (!customers.isSuccess) {
      return [];
    }
    let resp = customers.data.customers.filter((c) => !!c.name);
    if (filteredIds) {
      resp = resp.filter((c) => !filteredIds.includes(c.id));
    }
    resp = sortBy(resp, (_) => _.name);
    return resp;
  }, [customers.isSuccess]);

  if (!customers.isSuccess) {
    return <Skeleton variant="rectangular" height={29} />;
  }

  return (
    <VirtualAutoComplete
      formatter={Formatter}
      onChange={(e, newValue: StripeApiCustomerDto) => {
        onChange(newValue);
      }}
      componentsProps={{
        paper: {
          sx: {
            "&.MuiPaper-root": {
              width: "275px",
            },
          },
        },
        popper: {
          sx: {
            zIndex: 1800,
          },
        },
      }}
      getOptionDisabled={(option) =>
        !!(
          user.data?.user.email &&
          !["christian@intelligems.io", "samantha@intelligems.io"].includes(
            user.data.user.email
          ) &&
          (option.hasActiveStripeSubscription ||
            option.hasActiveShopifySubscription)
        )
      }
      isOptionEqualToValue={(option, value) => option.id === value.id}
      options={data}
      getOptionLabel={(option: StripeApiCustomerDto) => option.name}
      renderInput={(params) => <IgTextField {...params} variant={"standard"} />}
      value={payload.customer}
      filterOptions={filterByNameOrId}
    />
  );
};
